import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Phone,
  ChevronDown,
  CheckCircle2,
  Laptop,
  Mail,
  Database,
  Globe,
  ShieldCheck,
  Wifi,
  Clock,
  UserRound,
  Home as HomeIcon,
  MapPin,
  ArrowRight,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { getPostsByTag } from "@/data/blog-posts";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";

const LOCATIONS = [
  "Montclair, CA",
  "Rancho Cucamonga, CA",
  "Ontario, CA",
  "Upland, CA",
  "Claremont, CA",
  "Chino, CA",
  "Pomona, CA",
  "Inland Empire & Orange County",
];

const TITLE = "Tech Support for Realtors | SimpleTech";
const DESCRIPTION =
  "Fast, reliable tech support for real estate agents and brokerages. SimpleTech keeps realtors connected and deals closing. Call (909) 255-9112.";
const CANONICAL = canonical("/services/tech-support-for-realtors");

const FAQS = [
  {
    q: "Do you provide remote tech support for real estate agents?",
    a: "Yes — most issues are resolved remotely the same day. SimpleTech provides secure remote support so you don't have to wait for an in-person visit.",
  },
  {
    q: "Can you help with CRM and MLS software issues?",
    a: "Absolutely. SimpleTech provides real estate software support including CRM setup and troubleshooting, MLS-related tech assistance, eSignature platforms, and transaction management tools.",
  },
  {
    q: "Do you support individual agents or just large brokerages?",
    a: "Both. We work with independent realtors, real estate teams, brokers, property managers, and full brokerages. Our support plans are sized and priced to fit your operation.",
  },
  {
    q: "How quickly do you respond to tech issues?",
    a: "Most requests receive a same-day response, and many remote issues are resolved within the hour. We know a tech problem in real estate can mean a missed lead, so we don't make you wait.",
  },
  {
    q: "How much does realtor tech support cost?",
    a: "SimpleTech offers both one-time support and ongoing monthly plans with transparent, flat-rate pricing — no surprise invoices. Call (909) 255-9112 for a quick quote tailored to your needs.",
  },
];

export const Route = createFileRoute("/services_/tech-support-for-realtors")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Tech Support for Realtors",
          description:
            "Fast, reliable tech support for real estate agents, teams, and brokerages.",
          provider: {
            "@type": "LocalBusiness",
            name: "SimpleTech",
            telephone: "+1-909-255-9112",
            url: SITE_URL,
            address: {
              "@type": "PostalAddress",
              streetAddress: "8880 N Benson Ave #121",
              addressLocality: "Montclair",
              addressRegion: "CA",
              postalCode: "91763",
              addressCountry: "US",
            },
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }),
      },
    ],
  }),
  component: RealtorTechSupportPage,
});

const SERVICES = [
  {
    Icon: Laptop,
    title: "Device & Computer Support",
    body: "A slow computer before a listing presentation is not an option. We fix slow laptops, set up printers and scanners, troubleshoot Wi-Fi at your office or home, and support your mobile devices and tablets — so every showing and closing goes smoothly.",
  },
  {
    Icon: Mail,
    title: "Email & Communication Setup",
    body: "We set up and support Google Workspace and Microsoft 365, sync your calendars across devices, configure shared team inboxes for your brokerage, and add spam filtering and security protection so client communications never get compromised.",
  },
  {
    Icon: Database,
    title: "Real Estate Software Support",
    body: "CRM not syncing? MLS portal throwing errors? eSignature platform acting up? We provide hands-on support for the tools real estate agents use every day — CRM platforms, transaction management systems, eSignature tools like DocuSign, and cloud storage organization.",
  },
  {
    Icon: Globe,
    title: "Website & Online Presence Help",
    body: "Your website is your 24/7 listing agent. We handle website updates and fixes, IDX feed troubleshooting, domain and hosting support, contact form repairs, and basic SEO and speed improvements — so your online presence keeps generating leads.",
  },
  {
    Icon: ShieldCheck,
    title: "Security & Data Protection",
    body: "Real estate transactions involve sensitive client data, financial info, and personal records. We set up data backup solutions, enforce password management best practices, remove malware and viruses, and assist with account recovery.",
  },
  {
    Icon: Wifi,
    title: "Remote Support — Help Wherever You Are",
    body: "Because realtors are always on the move, we offer fast remote support that resolves most issues without an in-person visit. We connect securely to your device and fix the problem — often in under 30 minutes.",
  },
];

const WHY_POINTS = [
  "Fast response times — most issues resolved same day",
  "Remote support available — no waiting for an in-person visit",
  "Plain-English explanations — no tech jargon",
  "Support for individuals, teams, and full brokerages",
  "Ongoing plans so your tech never falls behind",
  "A real local team that picks up the phone",
];

function RealtorTechSupportPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SiteHeader active="services" />
      <main id="main-content" className="flex-1">
        {/* Hero */}
        <section className="relative bg-[#0b0a3d] text-white overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0b0a3d] via-[#141432] to-[#1e1e5a] opacity-95" />
          <div className="relative z-10 max-w-5xl mx-auto px-6 py-20 md:py-28">
            <p className="text-sky-400 font-bold tracking-widest text-sm uppercase mb-4">
              Realtor Tech Support
            </p>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Tech Support for Realtors
            </h1>
            <p className="mt-5 text-xl md:text-2xl font-bold text-sky-400">
              We keep realtors connected and deals closing
            </p>
            <p className="mt-8 text-base md:text-lg text-white/85 leading-relaxed max-w-3xl">
              Every missed call, broken email, or slow laptop can cost you a lead — and in real estate, lost leads mean
              lost commissions. SimpleTech provides{" "}
              <strong className="text-white">fast, reliable tech support for realtors</strong> who need their technology
              to work as hard as they do. We serve real estate agents, teams, and brokerages with remote and on-site
              support that fits your schedule, not ours. No confusing jargon. No waiting three days for a callback.
              Just dependable tech support from a local team that picks up the phone.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <a
                href="tel:+19092559112"
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                <Phone className="w-4 h-4" /> CALL (909) 255-9112
              </a>
              <Link
                to="/contact"
                className="inline-block bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                REQUEST SUPPORT
              </Link>
            </div>
            <p className="mt-5 text-sm text-white/70">We answer when you need us.</p>
          </div>
        </section>

        {/* Why */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              Why Realtors Trust SimpleTech
            </h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              You're showing homes in the morning, writing offers at noon, and answering client calls by 3pm. You don't
              have time for tech problems — and you definitely don't have time to wait on hold with a national IT
              helpdesk that doesn't know your business. SimpleTech was built for exactly that pace. Most issues are
              resolved the same day — many within the hour.
            </p>
            <ul className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-4">
              {WHY_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-slate-800">
                  <CheckCircle2 className="w-5 h-5 text-sky-500 mt-0.5 shrink-0" />
                  <span className="leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Services grid */}
        <section className="bg-slate-50 py-20 md:py-24">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              Tech Support Services for Real Estate Agents
            </h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              SimpleTech handles every layer of a realtor's technology stack — from the laptop you take to showings to
              the CRM that tracks your pipeline.
            </p>
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {SERVICES.map(({ Icon, title, body }) => (
                <div key={title} className="bg-white rounded-lg p-7 shadow-sm hover:shadow-md transition-shadow">
                  <Icon className="w-10 h-10 text-sky-500" strokeWidth={1.5} />
                  <h3 className="mt-5 text-xl font-extrabold text-[#0b0a3d]">{title}</h3>
                  <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">{body}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 text-slate-700 text-[15px] leading-relaxed max-w-3xl space-y-3">
              <p>
                Need a complete IT partner? See our{" "}
                <Link to="/services/managed-it-services" className="text-sky-600 font-semibold hover:underline">
                  managed IT services
                </Link>{" "}
                for end-to-end coverage, or our{" "}
                <Link
                  to="/services/$slug"
                  params={{ slug: "cyber-security" }}
                  className="text-sky-600 font-semibold hover:underline"
                >
                  cybersecurity services
                </Link>{" "}
                to protect client data. Building or refreshing your agent site? Check our{" "}
                <Link
                  to="/services/$slug"
                  params={{ slug: "website-design" }}
                  className="text-sky-600 font-semibold hover:underline"
                >
                  website design
                </Link>
                .
              </p>
              <p>
                Need a reliable phone system too? See our{" "}
                <Link
                  to="/services/$slug"
                  params={{ slug: "voip" }}
                  className="text-sky-600 font-semibold hover:underline"
                >
                  VoIP solutions for real estate offices
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        {/* Who we support */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">Who We Support</h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              SimpleTech provides realtor tech support for:
            </p>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { Icon: UserRound, label: "Independent real estate agents" },
                { Icon: HomeIcon, label: "Real estate teams (2–20 agents)" },
                { Icon: ShieldCheck, label: "Brokers and brokerages" },
                { Icon: Clock, label: "Property managers" },
                { Icon: Mail, label: "Real estate offices & admin staff" },
                { Icon: Database, label: "Transaction coordinators" },
              ].map(({ Icon, label }) => (
                <div key={label} className="flex items-center gap-4 p-5 rounded-md border border-slate-200">
                  <Icon className="w-8 h-8 text-sky-500 shrink-0" strokeWidth={1.5} />
                  <span className="font-semibold text-[#0b0a3d]">{label}</span>
                </div>
              ))}
            </div>
            <p className="mt-8 text-slate-700 leading-relaxed max-w-3xl">
              Whether you're a solo agent running your business from your phone or a managing broker overseeing a team
              of 15, SimpleTech has a support option that fits your size and your budget.
            </p>
          </div>
        </section>

        {/* Locations We Serve */}
        <section className="bg-slate-50 py-20 md:py-24">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              Locations We Serve
            </h2>
            <p className="mt-5 text-slate-700 text-base md:text-lg leading-relaxed max-w-3xl">
              SimpleTech supports real estate professionals across Southern California — with same-day remote help
              everywhere we work, and on-site visits across the areas below.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {LOCATIONS.map((loc) => (
                <div
                  key={loc}
                  className="flex items-center gap-3 bg-white rounded-md border border-slate-200 px-4 py-3"
                >
                  <MapPin className="w-5 h-5 text-sky-500 shrink-0" />
                  <span className="font-semibold text-[#0b0a3d] text-sm">{loc}</span>
                </div>
              ))}
            </div>
            <p className="mt-8 text-slate-700 leading-relaxed max-w-3xl">
              Don't see your city?{" "}
              <a href="tel:+19092559112" className="text-sky-600 font-semibold hover:underline">
                Call (909) 255-9112
              </a>{" "}
              — we likely cover it too.
            </p>
          </div>
        </section>

        {/* From the Blog — auto-pulls posts tagged "real-estate" */}
        <BlogSection />



        {/* CTA band */}
        <section className="bg-[#0b0a3d] text-white py-20 md:py-24">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">
              Get Fast Realtor Tech Support Today
            </h2>
            <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed">
              Technology shouldn't slow down your business. If your tech is causing headaches — or you just want a
              reliable partner to keep everything running — SimpleTech is ready to help.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a
                href="tel:+19092559112"
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                <Phone className="w-4 h-4" /> CALL (909) 255-9112
              </a>
              <Link
                to="/contact"
                className="inline-block bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
              >
                CONTACT US
              </Link>
            </div>
            <p className="mt-6 text-sm text-white/70">No rushing. No jargon. A team that answers the phone.</p>
          </div>
        </section>

        {/* FAQs */}
        <section className="bg-white py-20 md:py-24">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight text-center">
              Frequently Asked Questions
            </h2>
            <div className="mt-12 space-y-3">
              {FAQS.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={f.q} className="border border-slate-200 rounded-md overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(open ? null : i)}
                      className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-slate-50 transition-colors"
                      aria-expanded={open}
                    >
                      <span className="font-bold text-[#0b0a3d]">{f.q}</span>
                      <ChevronDown
                        className={`w-5 h-5 text-sky-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                      />
                    </button>
                    {open && (
                      <div className="px-5 pb-5 text-slate-700 leading-relaxed">{f.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function BlogSection() {
  const posts = getPostsByTag("real-estate");
  if (posts.length === 0) return null;
  return (
    <section className="bg-white py-20 md:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-[#0b0a3d] tracking-tight">
              Real Estate Tech Insights
            </h2>
            <p className="mt-4 text-slate-700 text-base md:text-lg leading-relaxed max-w-2xl">
              Guides, tips, and updates for real estate agents and brokerages — from our blog.
            </p>
          </div>
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-sky-600 font-bold hover:text-sky-700"
          >
            All articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.slice(0, 6).map((post) => (
            <Link
              key={post.slug}
              to="/articles/$slug"
              params={{ slug: post.slug }}
              className="group bg-slate-50 rounded-lg overflow-hidden border border-slate-200 hover:shadow-lg transition-shadow"
            >
              <div className="aspect-[16/9] overflow-hidden bg-slate-200">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <p className="text-xs font-bold text-sky-600 tracking-wider uppercase">{post.date}</p>
                <h3 className="mt-2 text-lg font-extrabold text-[#0b0a3d] group-hover:text-sky-600 transition-colors leading-tight">
                  {post.title}
                </h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">{post.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
